import { useCallback, useMemo, useRef, useState } from "react";
import {
	cloneDeliverables,
	getChronologyErrors,
	itemErrorKey,
	type DeliverableDraft,
	type DeliverableItem,
} from "./engagementTermsState";

const emptyDraft = (): DeliverableDraft => ({ name: "", deliverableTypes: [], dueDate: "" });
type DeliverableDraftField = keyof DeliverableDraft;

export const useEngagementDeliverables = () => {
	const [deliverables, setDeliverables] = useState<DeliverableItem[]>([]);
	const [invalidItems, setInvalidItems] = useState<Set<string>>(new Set());
	const [deliverableModal, setDeliverableModal] = useState<{ mode: "add" | "edit"; id?: number } | null>(null);
	const [deliverableDraft, setDeliverableDraft] = useState<DeliverableDraft>(emptyDraft);
	const [deliverableDraftErrors, setDeliverableDraftErrors] = useState<Set<DeliverableDraftField>>(new Set());
	const [goLiveEditingId, setGoLiveEditingId] = useState<number | null>(null);
	const [goLiveDraft, setGoLiveDraft] = useState("");
	const [goLiveError, setGoLiveError] = useState("");
	const nextDeliverableIdRef = useRef(1);
	const nextItemIdRef = useRef(1);
	const draggedItemRef = useRef<{ deliverableId: number; itemId: number } | null>(null);
	const chronologyErrors = useMemo(() => getChronologyErrors(deliverables), [deliverables]);

	const hydrate = useCallback((nextDeliverables: DeliverableItem[]) => {
		const safeDeliverables = cloneDeliverables(nextDeliverables);
		setDeliverables(safeDeliverables);
		setInvalidItems(new Set());
		setDeliverableModal(null);
		setDeliverableDraftErrors(new Set());
		setGoLiveEditingId(null);
		setGoLiveError("");
		nextDeliverableIdRef.current = Math.max(0, ...safeDeliverables.map((item) => item.id)) + 1;
		nextItemIdRef.current = Math.max(0, ...safeDeliverables.flatMap((item) => item.items.map((row) => row.id))) + 1;
	}, []);

	const clearItemError = useCallback((key: string) => {
		setInvalidItems((current) => {
			if (!current.has(key)) return current;
			const next = new Set(current);
			next.delete(key);
			return next;
		});
	}, []);

	const updateDeliverableItem = useCallback(
		(deliverableId: number, itemId: number, field: "name" | "dueDate", value: string) => {
			setDeliverables((current) =>
				current.map((deliverable) =>
					deliverable.id === deliverableId
						? {
								...deliverable,
								items: deliverable.items.map((item) =>
									item.id === itemId ? { ...item, [field]: value } : item,
								),
							}
						: deliverable,
				),
			);
			if (value.trim()) clearItemError(itemErrorKey(deliverableId, itemId, field));
		},
		[clearItemError],
	);

	const addDeliverableItem = useCallback((deliverableId: number) => {
		const itemId = nextItemIdRef.current++;
		setDeliverables((current) =>
			current.map((deliverable) =>
				deliverable.id === deliverableId
					? { ...deliverable, items: [...deliverable.items, { id: itemId, name: "", dueDate: "" }] }
					: deliverable,
			),
		);
	}, []);

	const removeDeliverableItem = useCallback((deliverableId: number, itemId: number) => {
		setDeliverables((current) =>
			current.map((deliverable) => {
				if (deliverable.id !== deliverableId) return deliverable;
				const remaining = deliverable.items.filter((item) => item.id !== itemId);
				return {
					...deliverable,
					items: remaining.length ? remaining : [{ id: nextItemIdRef.current++, name: "", dueDate: "" }],
				};
			}),
		);
		setInvalidItems((current) => {
			const prefix = `${deliverableId}:${itemId}:`;
			return new Set([...current].filter((key) => !key.startsWith(prefix)));
		});
	}, []);

	const beginDrag = useCallback((deliverableId: number, itemId: number) => {
		draggedItemRef.current = { deliverableId, itemId };
	}, []);

	const endDrag = useCallback(() => {
		draggedItemRef.current = null;
	}, []);

	const dropDeliverableItem = useCallback((deliverableId: number, targetItemId: number) => {
		const dragged = draggedItemRef.current;
		draggedItemRef.current = null;
		if (!dragged || dragged.deliverableId !== deliverableId || dragged.itemId === targetItemId) return;
		setDeliverables((current) =>
			current.map((deliverable) => {
				if (deliverable.id !== deliverableId) return deliverable;
				const fromIndex = deliverable.items.findIndex((item) => item.id === dragged.itemId);
				const toIndex = deliverable.items.findIndex((item) => item.id === targetItemId);
				if (fromIndex < 0 || toIndex < 0) return deliverable;
				const items = [...deliverable.items];
				const [moved] = items.splice(fromIndex, 1);
				items.splice(toIndex, 0, moved);
				return { ...deliverable, items };
			}),
		);
	}, []);

	const openAddDeliverable = useCallback(() => {
		setDeliverableDraft({
			name: "",
			deliverableTypes: [],
			dueDate: "",
		});
		setDeliverableModal({ mode: "add" });
		setDeliverableDraftErrors(new Set());
	}, []);

	const openEditDeliverable = useCallback((deliverable: DeliverableItem, showValidation = false) => {
		setDeliverableDraft({
			name: deliverable.name,
			deliverableTypes: [...deliverable.deliverableTypes],
			dueDate: deliverable.dueDate,
		});
		setDeliverableModal({ mode: "edit", id: deliverable.id });
		setDeliverableDraftErrors(
			showValidation
				? new Set<DeliverableDraftField>([
						...(!deliverable.name.trim() ? (["name"] as const) : []),
						...(deliverable.deliverableTypes.length === 0 ? (["deliverableTypes"] as const) : []),
						...(!deliverable.dueDate ? (["dueDate"] as const) : []),
					])
				: new Set(),
		);
	}, []);

	const updateDeliverableDraft = useCallback(
		<K extends DeliverableDraftField>(field: K, value: DeliverableDraft[K]) => {
			setDeliverableDraft((current) => ({ ...current, [field]: value }));
			setDeliverableDraftErrors((current) => {
				if (!current.has(field)) return current;
				const next = new Set(current);
				next.delete(field);
				return next;
			});
		},
		[],
	);

	const confirmDeliverableModal = useCallback(() => {
		if (!deliverableModal) return;
		const errors = new Set<DeliverableDraftField>([
			...(!deliverableDraft.name.trim() ? (["name"] as const) : []),
			...(deliverableDraft.deliverableTypes.length === 0 ? (["deliverableTypes"] as const) : []),
			...(!deliverableDraft.dueDate ? (["dueDate"] as const) : []),
		]);
		setDeliverableDraftErrors(errors);
		if (errors.size) return;
		if (deliverableModal.mode === "add") {
			const deliverableId = nextDeliverableIdRef.current++;
			const itemId = nextItemIdRef.current++;
			setDeliverables((current) => {
				const overallLiveDate = current.at(-1)?.liveDate || "";
				return [
					...current.map((deliverable) => ({ ...deliverable, liveDate: "" })),
					{
						...deliverableDraft,
						id: deliverableId,
						liveDate: overallLiveDate,
						items: [{ id: itemId, name: "", dueDate: "" }],
					},
				];
			});
		} else if (deliverableModal.id != null) {
			setDeliverables((current) =>
				current.map((deliverable) =>
					deliverable.id === deliverableModal.id ? { ...deliverable, ...deliverableDraft } : deliverable,
				),
			);
		}
		setDeliverableModal(null);
		setDeliverableDraftErrors(new Set());
	}, [deliverableDraft, deliverableModal]);

	const openGoLiveModal = useCallback((deliverable: DeliverableItem, validationError = "") => {
		setGoLiveDraft(deliverable.liveDate);
		setGoLiveEditingId(deliverable.id);
		setGoLiveError(validationError);
	}, []);

	const confirmGoLiveModal = useCallback(() => {
		if (goLiveEditingId == null) return;
		if (!goLiveDraft) {
			setGoLiveError("Live date is required");
			return;
		}
		const finalDeliverableDate = deliverables[deliverables.length - 1]?.dueDate;
		if (finalDeliverableDate && goLiveDraft < finalDeliverableDate) {
			setGoLiveError("Live date cannot be before the final deliverable date");
			return;
		}
		setDeliverables((current) =>
			current.map((deliverable) =>
				deliverable.id === goLiveEditingId
					? { ...deliverable, liveDate: goLiveDraft }
					: { ...deliverable, liveDate: "" },
			),
		);
		setGoLiveEditingId(null);
	}, [deliverables, goLiveDraft, goLiveEditingId]);

	const updateGoLiveDraft = useCallback((value: string) => {
		setGoLiveDraft(value);
		setGoLiveError("");
	}, []);

	const validate = useCallback(() => {
		const requiredErrors = new Set<string>();
		const finalDeliverable = deliverables[deliverables.length - 1];
		const goLiveValidationError = !finalDeliverable
			? ""
			: !finalDeliverable.liveDate
				? "Live date is required"
				: finalDeliverable.dueDate && finalDeliverable.liveDate < finalDeliverable.dueDate
					? "Live date cannot be before the final deliverable date"
					: "";
		const invalidDeliverable = deliverables.find(
			(deliverable) =>
				!deliverable.name.trim() || deliverable.deliverableTypes.length === 0 || !deliverable.dueDate,
		);
		deliverables.forEach((deliverable) =>
			deliverable.items.forEach((item) => {
				if (!item.name.trim()) requiredErrors.add(itemErrorKey(deliverable.id, item.id, "name"));
				if (!item.dueDate) requiredErrors.add(itemErrorKey(deliverable.id, item.id, "dueDate"));
			}),
		);
		setInvalidItems(requiredErrors);
		return {
			valid:
				!invalidDeliverable &&
				requiredErrors.size === 0 &&
				chronologyErrors.size === 0 &&
				!goLiveValidationError,
			invalidDeliverable,
			invalidGoLiveDeliverable: goLiveValidationError ? finalDeliverable : undefined,
			goLiveValidationError,
			hasChronologyErrors: chronologyErrors.size > 0,
			firstErrorKey: (requiredErrors.values().next().value || chronologyErrors.values().next().value) as
				| string
				| undefined,
		};
	}, [chronologyErrors, deliverables]);

	return {
		deliverables,
		invalidItems,
		chronologyErrors,
		deliverableModal,
		setDeliverableModal,
		deliverableDraft,
		setDeliverableDraft,
		deliverableDraftErrors,
		updateDeliverableDraft,
		goLiveEditingId,
		setGoLiveEditingId,
		goLiveDraft,
		setGoLiveDraft,
		goLiveError,
		updateGoLiveDraft,
		hydrate,
		updateDeliverableItem,
		addDeliverableItem,
		removeDeliverableItem,
		beginDrag,
		endDrag,
		dropDeliverableItem,
		openAddDeliverable,
		openEditDeliverable,
		confirmDeliverableModal,
		openGoLiveModal,
		confirmGoLiveModal,
		validate,
	};
};
